import React, { useState, useEffect } from 'react'
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc, where, getDocs, writeBatch } from 'firebase/firestore'
import { db } from '../firebase'
import { useAuth } from '../contexts/AuthContext'
import { validateImageUrl } from '../utils/imageValidation'

export default function ProductMasterList() {
  const { userClaims } = useAuth()
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showAddProduct, setShowAddProduct] = useState(false)
  const [editingProduct, setEditingProduct] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [saving, setSaving] = useState(false)
  
  const [formData, setFormData] = useState({
    name: '',
    displayname: '',
    code: '',
    brand: '',
    MRP: '',
    imageUrl: ''
  })

  const [formErrors, setFormErrors] = useState({})
  const [showImportModal, setShowImportModal] = useState(false)
  const [importFile, setImportFile] = useState(null)
  const [importResults, setImportResults] = useState(null)
  const [importing, setImporting] = useState(false)
  const [showDisabled, setShowDisabled] = useState(true)
  const [togglingEnabled, setTogglingEnabled] = useState(new Set())

  // Real-time products listener
  useEffect(() => {
    if (!userClaims?.customer) return

    const collectionName = `${userClaims.customer}_products_master`
    const q = query(collection(db, collectionName), orderBy('displayname'))
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const productsData = []
      querySnapshot.forEach((doc) => {
        productsData.push({
          id: doc.id,
          ...doc.data()
        })
      })
      setProducts(productsData)
      setLoading(false)
    }, (err) => {
      console.error('Error fetching products:', err)
      setError(err.message)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [userClaims?.customer])

  // Filter products based on search term and showDisabled toggle
  const filteredProducts = products.filter(product => {
    if (!showDisabled && product.enabled === false) return false
    return (
      product.displayname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.brand?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })

  // Toggle enabled/disabled state for a product
  const handleToggleEnabled = async (product) => {
    if (togglingEnabled.has(product.id)) return
    setTogglingEnabled(prev => new Set([...prev, product.id]))
    try {
      const collectionName = `${userClaims.customer}_products_master`
      const productRef = doc(db, collectionName, product.id)
      const currentEnabled = product.enabled !== false
      await updateDoc(productRef, { enabled: !currentEnabled, updatedAt: new Date().toISOString() })
    } catch (err) {
      console.error('Error toggling product enabled state:', err)
      setError(err.message)
    } finally {
      setTogglingEnabled(prev => {
        const next = new Set(prev)
        next.delete(product.id)
        return next
      })
    }
  }

  // Validate form data
  const validateForm = () => {
    const errors = {}
    
    if (!formData.name.trim()) {
      errors.name = 'Product name is required'
    }
    
    if (!formData.displayname.trim()) {
      errors.displayname = 'Display name is required'
    }
    
    if (!formData.MRP || parseFloat(formData.MRP) <= 0) {
      errors.MRP = 'Valid MRP is required'
    }

    // Validate image URL if provided
    if (formData.imageUrl && formData.imageUrl.trim()) {
      const validation = validateImageUrl(formData.imageUrl)
      if (!validation.isValid) {
        errors.imageUrl = validation.error
      }
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Check if product code already exists
  const checkDuplicateCode = async (code, excludeId = null) => {
    if (!code.trim()) return false
    
    const collectionName = `${userClaims.customer}_products_master`
    const q = query(collection(db, collectionName), where('code', '==', code.trim()))
    const querySnapshot = await getDocs(q)
    
    return querySnapshot.docs.some(doc => doc.id !== excludeId)
  }

  // Check if product name already exists (case-insensitive)
  const checkDuplicateName = async (name, excludeId = null) => {
    if (!name.trim()) return false
    
    const collectionName = `${userClaims.customer}_products_master`
    const q = query(collection(db, collectionName))
    const querySnapshot = await getDocs(q)
    
    // Check for case-insensitive duplicate names
    return querySnapshot.docs.some(doc => 
      doc.id !== excludeId && 
      doc.data().name?.toLowerCase() === name.trim().toLowerCase()
    )
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) return

    setSaving(true)
    setError(null)

    try {
      // Check for duplicate name (required field)
      const isNameDuplicate = await checkDuplicateName(formData.name, editingProduct?.id)
      if (isNameDuplicate) {
        setFormErrors({ name: 'Product name already exists' })
        setSaving(false)
        return
      }

      // Check for duplicate code if provided
      if (formData.code.trim()) {
        const isCodeDuplicate = await checkDuplicateCode(formData.code, editingProduct?.id)
        if (isCodeDuplicate) {
          setFormErrors({ code: 'Product code already exists' })
          setSaving(false)
          return
        }
      }

      const collectionName = `${userClaims.customer}_products_master`
      
      const productData = {
        name: formData.name.trim(),
        displayname: formData.displayname.trim(),
        code: formData.code.trim() || '',
        brand: formData.brand.trim() || '',
        MRP: parseFloat(formData.MRP),
        imageUrl: formData.imageUrl.trim() || '',
        updatedAt: new Date().toISOString()
      }

      if (editingProduct) {
        // Update existing product (preserve enabled state)
        const productRef = doc(db, collectionName, editingProduct.id)
        await updateDoc(productRef, productData)
        console.log('Product updated successfully')
      } else {
        // Add new product — default to enabled
        productData.createdAt = new Date().toISOString()
        productData.enabled = true
        await addDoc(collection(db, collectionName), productData)
        console.log('Product added successfully')
      }

      // Close modal and reset form
      setFormData({ name: '', displayname: '', code: '', brand: '', MRP: '', imageUrl: '' })
      setFormErrors({})
      setShowAddProduct(false)
      setEditingProduct(null)
    } catch (err) {
      console.error('Error saving product:', err)
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  // Handle product edit
  const handleEdit = (product) => {
    setFormData({
      name: product.name || '',
      displayname: product.displayname || '',
      code: product.code || '',
      brand: product.brand || '',
      MRP: product.MRP?.toString() || '',
      imageUrl: product.imageUrl || ''
    })
    setEditingProduct(product)
    setFormErrors({})
    setShowAddProduct(true)
  }

  // Handle product delete
  const handleDelete = async (product) => {
    if (!window.confirm(`Are you sure you want to delete "${product.displayname}"? This action cannot be undone.`)) {
      return
    }

    setSaving(true)
    try {
      const collectionName = `${userClaims.customer}_products_master`
      await deleteDoc(doc(db, collectionName, product.id))
      console.log('Product deleted successfully')
    } catch (err) {
      console.error('Error deleting product:', err)
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  // Reset form and close modal
  const handleCancel = () => {
    setFormData({ name: '', displayname: '', code: '', brand: '', MRP: '', imageUrl: '' })
    setFormErrors({})
    setShowAddProduct(false)
    setEditingProduct(null)
  }

  // Shared CSV field escaping function
  const escapeCSVField = (value) => {
    if (value === null || value === undefined) return ''
    const stringValue = String(value)
    
    // Check if field needs quoting (contains comma, quote, newline, or leading/trailing spaces)
    const needsQuoting = stringValue.includes(',') || 
                        stringValue.includes('"') || 
                        stringValue.includes('\n') || 
                        stringValue.includes('\r') ||
                        stringValue.startsWith(' ') ||
                        stringValue.endsWith(' ')
    
    if (needsQuoting) {
      // Escape quotes by doubling them and wrap in quotes
      return `"${stringValue.replace(/"/g, '""')}"`
    }
    
    return stringValue
  }

  // Export products to CSV with proper escaping
  const handleExportCSV = () => {
    const headers = ['name', 'displayname', 'code', 'brand', 'MRP', 'imageUrl', 'enabled']
    
    const csvContent = [
      headers.join(','),
      ...products.map(product => {
        const row = headers.map(header => {
          if (header === 'enabled') return escapeCSVField(product.enabled !== false ? 'true' : 'false')
          return escapeCSVField(product[header])
        })
        return row.join(',')
      })
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `products_${userClaims.customer}_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Download CSV template with proper escaping
  const handleDownloadTemplate = () => {
    const headers = ['name', 'displayname', 'code', 'brand', 'MRP', 'imageUrl', 'enabled']
    const sampleData = {
      name: 'coca_cola_330ml',
      displayname: 'Coca Cola 330ml',
      code: 'CC330',
      brand: 'Coca Cola',
      MRP: '25.00',
      imageUrl: 'https://example.com/coca-cola.jpg',
      enabled: 'true'
    }
    
    const csvContent = [
      headers.join(','),
      headers.map(header => escapeCSVField(sampleData[header])).join(',')
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', 'product_template.csv')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Parse CSV file with proper quote handling
  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim())
    if (lines.length < 2) return { headers: [], data: [] }
    
    // Parse headers
    const headers = parseCSVLine(lines[0])
    
    // Parse data rows
    const data = lines.slice(1).map(line => parseCSVLine(line))
    
    return { headers, data }
  }
  
  // Helper function to parse a single CSV line
  const parseCSVLine = (line) => {
    const values = []
    let current = ''
    let inQuotes = false
    let i = 0
    
    while (i < line.length) {
      const char = line[i]
      
      if (char === '"') {
        if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
          // Escaped quote inside quoted field
          current += '"'
          i += 2
        } else {
          // Start or end of quoted field
          inQuotes = !inQuotes
          i++
        }
      } else if (char === ',' && !inQuotes) {
        // Field separator
        values.push(current.trim())
        current = ''
        i++
      } else {
        current += char
        i++
      }
    }
    
    // Add the last field
    values.push(current.trim())
    
    return values
  }

  // Validate CSV data
  const validateCSVData = (headers, data) => {
    const requiredHeaders = ['name', 'displayname', 'MRP']
    const validationResults = {
      valid: [],
      errors: [],
      duplicates: [],
      warnings: []
    }
    
    // Check required headers
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h))
    if (missingHeaders.length > 0) {
      validationResults.errors.push(`Missing required columns: ${missingHeaders.join(', ')}`)
      return validationResults
    }
    
    // Track codes and names for duplicate checking
    const existingCodes = new Set(products.map(p => p.code).filter(Boolean))
    const csvCodes = new Set()
    // Track names with case-insensitive checking (convert to lowercase)
    const existingNames = new Set(products.map(p => p.name?.toLowerCase()).filter(Boolean))
    const csvNames = new Set()
    
    data.forEach((row, index) => {
      const rowNum = index + 2 // +2 because index starts at 0 and we skip header
      const product = {}
      
      headers.forEach((header, i) => {
        product[header] = row[i] || ''
      })
      
      const errors = []
      const warnings = []
      
      // Validate required fields
      if (!product.name?.trim()) {
        errors.push('Product name is required')
      }
      if (!product.displayname?.trim()) {
        errors.push('Display name is required')
      }
      if (!product.MRP || isNaN(parseFloat(product.MRP)) || parseFloat(product.MRP) <= 0) {
        errors.push('Valid MRP is required')
      }
      
      // Validate data types
      if (product.MRP && !isNaN(parseFloat(product.MRP))) {
        product.MRP = parseFloat(product.MRP)
      }
      
      // Check for duplicate names (case-insensitive)
      if (product.name?.trim()) {
        const name = product.name.trim().toLowerCase()
        if (existingNames.has(name)) {
          validationResults.duplicates.push(`Row ${rowNum}: Product name "${product.name.trim()}" already exists in database`)
        } else if (csvNames.has(name)) {
          validationResults.duplicates.push(`Row ${rowNum}: Duplicate product name "${product.name.trim()}" in CSV file`)
        } else {
          csvNames.add(name)
        }
      }

      // Check for duplicate codes
      if (product.code?.trim()) {
        const code = product.code.trim()
        if (existingCodes.has(code)) {
          validationResults.duplicates.push(`Row ${rowNum}: Code "${code}" already exists in database`)
        } else if (csvCodes.has(code)) {
          validationResults.duplicates.push(`Row ${rowNum}: Duplicate code "${code}" in CSV file`)
        } else {
          csvCodes.add(code)
        }
      }
      
      // Validate image URL format
      if (product.imageUrl?.trim()) {
        try {
          new URL(product.imageUrl.trim())
        } catch {
          warnings.push('Invalid image URL format')
        }
      }
      
      if (errors.length === 0) {
        validationResults.valid.push({ ...product, rowNum })
      } else {
        validationResults.errors.push(`Row ${rowNum}: ${errors.join(', ')}`)
      }
      
      if (warnings.length > 0) {
        validationResults.warnings.push(`Row ${rowNum}: ${warnings.join(', ')}`)
      }
    })
    
    return validationResults
  }

  // Handle file import
  const handleFileImport = (event) => {
    const file = event.target.files[0]
    if (!file) return
    
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a CSV file')
      return
    }
    
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const text = e.target.result
        const { headers, data } = parseCSV(text)
        const results = validateCSVData(headers, data)
        setImportResults(results)
        setImportFile(file)
      } catch (err) {
        setError('Error reading CSV file: ' + err.message)
      }
    }
    reader.readAsText(file)
  }

  // Import validated products
  const handleImportProducts = async () => {
    if (!importResults?.valid?.length) return
    
    setImporting(true)
    try {
      const collectionName = `${userClaims.customer}_products_master`
      const batch = writeBatch(db)
      
      importResults.valid.forEach(product => {
        const docRef = doc(collection(db, collectionName))
        const enabledValue = product.enabled !== undefined
          ? String(product.enabled).toLowerCase() !== 'false'
          : true
        batch.set(docRef, {
          name: product.name.trim(),
          displayname: product.displayname.trim(),
          code: product.code?.trim() || '',
          brand: product.brand?.trim() || '',
          MRP: product.MRP,
          imageUrl: product.imageUrl?.trim() || '',
          enabled: enabledValue,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })
      })
      
      await batch.commit()
      console.log(`Successfully imported ${importResults.valid.length} products`)
      
      // Reset import state
      setShowImportModal(false)
      setImportFile(null)
      setImportResults(null)
      setImporting(false)
    } catch (err) {
      console.error('Error importing products:', err)
      setError('Error importing products: ' + err.message)
      setImporting(false)
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <div className="text-center">
          <div className="spinner-border mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div>Loading product master list...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="container-fluid">
      {error && (
        <div className="alert alert-danger alert-dismissible fade show">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">Product Master List</h4>
              <p className="text-muted mb-0">Manage your product catalog for consistent slot configuration</p>
            </div>
            <div className="btn-group">
              <button
                className="btn btn-outline-secondary"
                onClick={handleDownloadTemplate}
                title="Download CSV template"
              >
                <i className="fas fa-download me-2"></i>
                Template
              </button>
              <button
                className="btn btn-outline-primary"
                onClick={handleExportCSV}
                disabled={products.length === 0}
                title="Export products to CSV"
              >
                <i className="fas fa-file-export me-2"></i>
                Export
              </button>
              <button
                className="btn btn-outline-success"
                onClick={() => setShowImportModal(true)}
                title="Import products from CSV"
              >
                <i className="fas fa-file-import me-2"></i>
                Import
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setShowAddProduct(true)}
              >
                <i className="fas fa-plus me-2"></i>
                Add Product
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Stats */}
      <div className="row mb-4">
        <div className="col-md-8">
          <div className="input-group">
            <span className="input-group-text">
              <i className="fas fa-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search products by name, code, or brand..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="mt-2">
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="checkbox"
                id="showDisabledCheck"
                checked={showDisabled}
                onChange={(e) => setShowDisabled(e.target.checked)}
              />
              <label className="form-check-label text-muted small" htmlFor="showDisabledCheck">
                Show disabled products
              </label>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card bg-light border-0 h-100">
            <div className="card-body text-center py-2">
              <div className="fw-bold text-primary">{products.filter(p => p.enabled !== false).length}</div>
              <small className="text-muted">Active Products</small>
              {products.some(p => p.enabled === false) && (
                <div>
                  <small className="text-danger">{products.filter(p => p.enabled === false).length} disabled</small>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="card">
        <div className="card-body">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-box-open fa-3x text-muted mb-3"></i>
              <div className="h5 text-muted">
                {searchTerm ? 'No products found matching your search' : 'No products in master list'}
              </div>
              <div className="text-muted">
                {searchTerm ? 'Try adjusting your search terms' : 'Add your first product to get started'}
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Image</th>
                    <th>Display Name</th>
                    <th>Product Name</th>
                    <th>Code</th>
                    <th>Brand</th>
                    <th>MRP</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => {
                    const isDisabled = product.enabled === false
                    const isToggling = togglingEnabled.has(product.id)
                    return (
                    <tr key={product.id} className={isDisabled ? 'table-secondary text-muted' : ''}>
                      <td style={{ opacity: isDisabled ? 0.5 : 1 }}>
                        {product.imageUrl ? (
                          <img 
                            src={product.imageUrl} 
                            alt={product.displayname}
                            style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                            className="rounded border"
                            onError={(e) => {
                              e.target.style.display = 'none'
                              e.target.nextSibling.style.display = 'inline'
                            }}
                          />
                        ) : (
                          <div 
                            className="d-flex align-items-center justify-content-center bg-light border rounded text-muted"
                            style={{ width: '40px', height: '40px', fontSize: '12px' }}
                          >
                            <i className="fas fa-image"></i>
                          </div>
                        )}
                        <span style={{ display: 'none' }}>
                          <i className="fas fa-image text-muted"></i>
                        </span>
                      </td>
                      <td>
                        <div className="fw-bold">
                          {product.displayname}
                          {isDisabled && (
                            <span className="badge bg-secondary ms-2" style={{ fontSize: '0.7em' }}>Disabled</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="text-muted small">{product.name}</div>
                      </td>
                      <td>
                        <span className="badge bg-light text-dark">{product.code || '-'}</span>
                      </td>
                      <td>{product.brand || '-'}</td>
                      <td>
                        <span className="fw-bold">₹{product.MRP?.toFixed(2) || '0.00'}</span>
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button
                            className={`btn ${isDisabled ? 'btn-outline-success' : 'btn-outline-secondary'}`}
                            onClick={() => handleToggleEnabled(product)}
                            disabled={isToggling}
                            title={isDisabled ? 'Enable Product' : 'Disable Product'}
                          >
                            {isToggling
                              ? <i className="fas fa-spinner fa-spin"></i>
                              : <i className={`fas ${isDisabled ? 'fa-eye' : 'fa-eye-slash'}`}></i>
                            }
                          </button>
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => handleEdit(product)}
                            title="Edit Product"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDelete(product)}
                            disabled={saving}
                            title="Delete Product"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* CSV Import Modal */}
      {showImportModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-file-import me-2"></i>
                  Import Products from CSV
                </h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowImportModal(false)
                  setImportFile(null)
                  setImportResults(null)
                }}></button>
              </div>
              <div className="modal-body">
                {!importResults && (
                  <div>
                    <div className="alert alert-info">
                      <i className="fas fa-info-circle me-2"></i>
                      <strong>CSV Format Requirements:</strong>
                      <ul className="mb-0 mt-2">
                        <li>Required columns: <code>name</code>, <code>displayname</code>, <code>MRP</code></li>
                        <li>Optional columns: <code>code</code>, <code>brand</code>, <code>imageUrl</code></li>
                        <li>Use comma-separated values (CSV format)</li>
                        <li>First row should contain column headers</li>
                        <li>Values with commas, quotes, or spaces should be enclosed in double quotes</li>
                        <li>Use double quotes ("") to escape quotes within quoted fields</li>
                      </ul>
                    </div>
                    
                    <div className="mb-3">
                      <label className="form-label">Select CSV File</label>
                      <input
                        type="file"
                        className="form-control"
                        accept=".csv"
                        onChange={handleFileImport}
                      />
                      <div className="form-text">
                        Don't have a CSV file? <button 
                          type="button" 
                          className="btn btn-link p-0"
                          onClick={handleDownloadTemplate}
                        >
                          Download the template
                        </button> to get started. The template includes proper CSV formatting with quoted values.
                      </div>
                    </div>
                  </div>
                )}

                {importResults && (
                  <div>
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <div className="card bg-success text-white">
                          <div className="card-body text-center py-2">
                            <div className="h4 mb-0">{importResults.valid.length}</div>
                            <small>Valid Products</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card bg-danger text-white">
                          <div className="card-body text-center py-2">
                            <div className="h4 mb-0">{importResults.errors.length}</div>
                            <small>Errors</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card bg-warning text-dark">
                          <div className="card-body text-center py-2">
                            <div className="h4 mb-0">{importResults.duplicates.length}</div>
                            <small>Duplicates</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card bg-info text-white">
                          <div className="card-body text-center py-2">
                            <div className="h4 mb-0">{importResults.warnings.length}</div>
                            <small>Warnings</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {importResults.errors.length > 0 && (
                      <div className="alert alert-danger">
                        <h6><i className="fas fa-exclamation-triangle me-2"></i>Validation Errors</h6>
                        <ul className="mb-0">
                          {importResults.errors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {importResults.duplicates.length > 0 && (
                      <div className="alert alert-warning">
                        <h6><i className="fas fa-copy me-2"></i>Duplicate Codes Found</h6>
                        <ul className="mb-0">
                          {importResults.duplicates.map((duplicate, index) => (
                            <li key={index}>{duplicate}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {importResults.warnings.length > 0 && (
                      <div className="alert alert-info">
                        <h6><i className="fas fa-info-circle me-2"></i>Warnings</h6>
                        <ul className="mb-0">
                          {importResults.warnings.map((warning, index) => (
                            <li key={index}>{warning}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {importResults.valid.length > 0 && (
                      <div className="alert alert-success">
                        <h6><i className="fas fa-check-circle me-2"></i>Valid Products Ready for Import</h6>
                        <div className="table-responsive" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          <table className="table table-sm mb-0">
                            <thead>
                              <tr>
                                <th>Row</th>
                                <th>Display Name</th>
                                <th>Name</th>
                                <th>Code</th>
                                <th>MRP</th>
                              </tr>
                            </thead>
                            <tbody>
                              {importResults.valid.map((product, index) => (
                                <tr key={index}>
                                  <td>{product.rowNum}</td>
                                  <td>{product.displayname}</td>
                                  <td>{product.name}</td>
                                  <td>{product.code || '-'}</td>
                                  <td>₹{product.MRP}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowImportModal(false)
                    setImportFile(null)
                    setImportResults(null)
                  }}
                >
                  Cancel
                </button>
                {importResults && importResults.valid.length > 0 && (
                  <button 
                    type="button" 
                    className="btn btn-success" 
                    onClick={handleImportProducts}
                    disabled={importing || importResults.errors.length > 0}
                  >
                    {importing ? (
                      <>
                        <i className="fas fa-spinner fa-spin me-2"></i>
                        Importing...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check me-2"></i>
                        Import {importResults.valid.length} Products
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Product Modal */}
      {showAddProduct && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingProduct ? 'Edit Product' : 'Add New Product'}
                </h5>
                <button type="button" className="btn-close" onClick={handleCancel}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Product Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          placeholder="Internal product name/SKU"
                        />
                        {formErrors.name && (
                          <div className="invalid-feedback">{formErrors.name}</div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Display Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control ${formErrors.displayname ? 'is-invalid' : ''}`}
                          value={formData.displayname}
                          onChange={(e) => setFormData({...formData, displayname: e.target.value})}
                          placeholder="Customer-facing name"
                        />
                        {formErrors.displayname && (
                          <div className="invalid-feedback">{formErrors.displayname}</div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Product Code</label>
                        <input
                          type="text"
                          className={`form-control ${formErrors.code ? 'is-invalid' : ''}`}
                          value={formData.code}
                          onChange={(e) => setFormData({...formData, code: e.target.value})}
                          placeholder="Barcode/SKU code"
                        />
                        {formErrors.code && (
                          <div className="invalid-feedback">{formErrors.code}</div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Brand</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.brand}
                          onChange={(e) => setFormData({...formData, brand: e.target.value})}
                          placeholder="Brand/Manufacturer"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">
                      MRP (₹) <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className={`form-control ${formErrors.MRP ? 'is-invalid' : ''}`}
                      value={formData.MRP}
                      onChange={(e) => setFormData({...formData, MRP: e.target.value})}
                      placeholder="Maximum Retail Price"
                      min="0"
                      step="0.01"
                    />
                    {formErrors.MRP && (
                      <div className="invalid-feedback">{formErrors.MRP}</div>
                    )}
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Product Image URL</label>
                    <input
                      type="url"
                      className={`form-control ${formErrors.imageUrl ? 'is-invalid' : ''}`}
                      value={formData.imageUrl}
                      onChange={(e) => {
                        const newUrl = e.target.value
                        setFormData({...formData, imageUrl: newUrl})
                        // Clear imageUrl error on change
                        if (formErrors.imageUrl) {
                          setFormErrors({...formErrors, imageUrl: undefined})
                        }
                      }}
                      placeholder="https://example.com/product-image.jpg"
                    />
                    {formErrors.imageUrl && (
                      <div className="invalid-feedback">{formErrors.imageUrl}</div>
                    )}
                    <small className="text-muted">
                      Only HTTP/HTTPS URLs are accepted. Data URIs are not allowed.
                    </small>
                    {formData.imageUrl && !formErrors.imageUrl && (
                      <div className="mt-2">
                        <img 
                          src={formData.imageUrl} 
                          alt="Product preview"
                          style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                          className="rounded border"
                          onError={(e) => {
                            e.target.style.display = 'none'
                            e.target.nextSibling.style.display = 'block'
                          }}
                        />
                        <div className="text-danger small mt-1" style={{ display: 'none' }}>
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          Invalid image URL
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    <strong>Required fields:</strong> Product Name, Display Name, and MRP are mandatory.
                    {!editingProduct && (
                      <div className="mt-2">
                        <strong>Bulk Import:</strong> Need to add many products? Use the <strong>Import</strong> button to upload a CSV file.
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? (
                      <>
                        <i className="fas fa-spinner fa-spin me-2"></i>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        {editingProduct ? 'Update Product' : 'Add Product'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}